import { useEffect } from "react";

import { useQueryClient, UseQueryResult } from "@tanstack/react-query";

import { HookResult } from "@fartherfinance/frontend/api/Types";

export interface UseGlobalPollingOptions<
  T extends (...args: any) => HookResult<any> | UseQueryResult<any>
> {
  useQueryHook: T;
  hookParams: Parameters<T>;
  queryKey: string[];
}

interface GlobalPollingComponentProps<
  T extends (...args: any) => HookResult<any>,
  R = ReturnType<T>
> {
  pollingOptions: UseGlobalPollingOptions<T>;
  onSuccess: (data: HookResult<R>) => HookResult<R>;
  onError: (data: HookResult<R>) => HookResult<R>;
}

const GlobalPollingComponent = <T extends (...args: any) => HookResult<any>>({
  pollingOptions,
  onError,
  onSuccess,
}: GlobalPollingComponentProps<T>) => {
  const { useQueryHook, hookParams, queryKey } = pollingOptions;

  const queryResult = useQueryHook(...hookParams) as HookResult<any>;

  const queryClient = useQueryClient();

  useEffect(() => {
    if (queryResult?.data !== undefined && queryResult.data.data !== null) {
      queryClient.invalidateQueries({ queryKey });

      if (queryResult.hasError) {
        onError(queryResult);
      } else {
        onSuccess(queryResult);
      }
    }
  }, [queryClient, queryKey, queryResult, onError, onSuccess]);

  return null;
};

export default GlobalPollingComponent;
